/**
 * ScreenCaptureService - 하이브리드 화면 캡처 서비스
 *
 * "Capture First, Report Later" 패턴의 핵심.
 *
 * 전략 (권한 없는 빠른 캡처 우선):
 * 1순위: modern-screenshot (DOM 기반, 권한 불필요, CSS 변수 해석 지원)
 *         → html2canvas/html-to-image 대비 CSS custom properties, 폰트, 레이아웃 정확도 높음
 *         → 교차 출처 리소스 폴백 처리 내장
 * 2순위: Capacitor 네이티브 — iOS/Android WebView 스냅샷 (향후)
 *
 * 캡처 시 data-screenshot-exclude 속성이 있는 요소는 자동 제외됩니다.
 */

import { Capacitor } from '@capacitor/core';

class ScreenCaptureService {
  /**
   * 현재 화면을 즉시 캡처합니다.
   * UI 전환(모달 열기 등) 전에 호출해야 합니다.
   */
  async captureScreen(): Promise<Blob> {
    if (Capacitor.isNativePlatform()) {
      return this.captureNative();
    }
    return this.captureWeb();
  }

  /**
   * Web/PWA: modern-screenshot
   *
   * modern-screenshot는 html-to-image의 개선 포크로:
   * - CSS custom properties (var(--...)) 해석 지원
   * - 교차 출처 CSS/이미지 graceful 폴백 (크래시 없음)
   * - Web font 안전 처리
   * - computedStyle을 인라인하여 렌더링 정확도 극대화
   */
  private async captureWeb(): Promise<Blob> {
    const { domToBlob } = await import('modern-screenshot');

    // document.body를 캡처 대상으로 사용하여
    // Radix Portal(팝오버, 드롭다운, 모달 등)도 캡처에 포함
    const root = document.body;

    const blob = await domToBlob(root, {
      // 해상도
      scale: Math.min(window.devicePixelRatio, 2),

      // 뷰포트 영역
      width: window.innerWidth,
      height: window.innerHeight,

      // 배경색: CSS 변수 resolved 값 적용
      backgroundColor: getComputedStyle(document.documentElement).backgroundColor || '#ffffff',

      // 스타일 오버라이드 (필요 시)
      style: null,

      // data-screenshot-exclude 요소 제외
      filter: (el: Node) => {
        if (el instanceof HTMLElement && el.dataset?.screenshotExclude !== undefined) {
          return false;
        }
        return true;
      },

      // 교차 출처 리소스 fetch 옵션
      fetch: {
        requestInit: {
          mode: 'cors',
          credentials: 'omit',
        },
      },

      // 웹폰트 임베딩 (CORS 에러 시 자동 스킵)
      font: {},
    });

    if (!blob) throw new Error('Screenshot capture returned null');
    return blob;
  }

  /**
   * Native: Capacitor 플러그인으로 WebView 스냅샷
   */
  private async captureNative(): Promise<Blob> {
    // TODO: Capacitor 네이티브 스크린샷 플러그인 연동
    console.warn('Native screenshot plugin not available, falling back to web capture');
    return this.captureWeb();
  }

  /**
   * base64 문자열을 Blob으로 변환 (네이티브 캡처용)
   */
  base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  }
}

export const screenCapture = new ScreenCaptureService();
